import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { PlaceholderKey, isKnownPlaceholderKey } from './placeholder-keys';
import { formatPlayerNameParts, type PlayerNameParts } from './player-name-format';
import type { PlaceholderData } from './types';

/**
 * A team's row in a standings table. Used only by the TOURNAMENT_TABLE
 * (match-less) resolver context — match templates no longer carry standing
 * data (see StandingsResolverContext below).
 */
export interface ResolverTeamStanding {
  position?: number;
  gamesPlayed?: number;
  gamesWon?: number;
  gamesLost?: number;
  gamesDrawn?: number;
  goalsScored?: number;
  goalsConceded?: number;
  points?: number;
}

export interface ResolverTeam {
  id: number;
  name: string;
  shortName: string;
  logoUrl?: string;
  standing?: ResolverTeamStanding;
}

export interface ResolverChampionship {
  id: number;
  name: string;
  logoUrl?: string;
  startDate?: string;
}

export interface ResolverTournament {
  id: number;
  name: string;
  logoUrl?: string;
}

export interface ResolverMatch {
  id: number;
  date?: string;
  venue?: string;
  homeScore?: number;
  awayScore?: number;
  roundName?: string;
}

export interface ResolverPlayer {
  id: number;
  /**
   * The player's full name, already assembled. Used as-is when `nameParts` is
   * absent, and as the fallback when a chosen format yields nothing.
   */
  name: string;
  /**
   * Structured parts, when the source can supply them. Present, a placeholder's
   * `nameFormat` can render any preset (surname only, "И. Петров", …); absent,
   * `name` is rendered verbatim regardless of format — a flattened name cannot
   * be split back into parts reliably.
   */
  nameParts?: PlayerNameParts;
  number?: number;
  photoUrl?: string;
}

/**
 * A single match slot for multi-match templates (MULTIPLE_SCHEDULE,
 * MULTIPLE_RESULTS). Each slot is the full per-match bundle the resolver
 * needs — the match itself plus its two teams. A placeholder with
 * `matchNumber=N` resolves against `matches[N-1]`.
 */
export interface ResolverMatchSlot {
  match: ResolverMatch;
  homeTeam: ResolverTeam;
  awayTeam: ResolverTeam;
  homeRoster?: ResolverPlayer[];
  awayRoster?: ResolverPlayer[];
}

/** The goal a video goal plate is about (match video review, IMGM-1606). */
export interface ResolverGoalMoment {
  /** The team the goal counts for — for an own goal, not the scorer's team. */
  team: ResolverTeam;
  /** `undefined` — the protocol has no scorer for it. */
  scorer?: ResolverPlayer;
  /** «19′», «45+2′». */
  minute: string;
}

export interface MatchResolverContext {
  /**
   * Required, not optional. With three variants in the union an optional `mode`
   * would make `match` the implicit default at every dispatch, so a new context
   * type would silently resolve against match data instead of failing loudly —
   * and TypeScript could not narrow the union at all.
   */
  mode: 'match';
  tournament: ResolverTournament;
  championship: ResolverChampionship;
  match: ResolverMatch;
  homeTeam: ResolverTeam;
  awayTeam: ResolverTeam;
  homeRoster?: ResolverPlayer[];
  awayRoster?: ResolverPlayer[];
  /**
   * Multi-match payload. Populated only for templates that render several
   * matches at once (MULTIPLE_SCHEDULE, MULTIPLE_RESULTS). When set, a
   * placeholder with `matchNumber=N` resolves against `matches[N-1]`;
   * placeholders with no `matchNumber` still fall back to the top-level
   * single-match fields. `matches[0]` typically mirrors the top-level
   * single-match fields so legacy templates keep rendering unchanged.
   */
  matches?: readonly ResolverMatchSlot[];
  /**
   * Active template subtypes (e.g. indicator subtypes for the top-players
   * template). Forwarded by the render service from the template row so
   * subtype-aware placeholder keys added later can branch on it. Empty when
   * the active type has no subtype dimension.
   */
  templateSubtypes?: readonly string[];
  /**
   * Set only on a video goal plate. The goal plate addresses nothing: its team keys read the team the goal counts for,
   * its player keys the scorer, `SCORE_TEAM` that team's goals; `match.homeScore` / `awayScore` hold the score right
   * after this goal.
   */
  goal?: ResolverGoalMoment;
}

/**
 * A stage or a single group's standings table — used by TOURNAMENT_TABLE
 * templates, which have no match. A placeholder's `teamNumber` addresses a
 * row/position in `standingTeams` (1 = top of table) instead of home/away.
 */
export interface ResolverStage {
  id: number;
  name: string;
  type: string;
}

export interface ResolverStandingsGroup {
  id: number;
  name: string;
}

export interface StandingsResolverContext {
  mode: 'standings';
  tournament: ResolverTournament;
  championship: ResolverChampionship;
  stage: ResolverStage;
  /** Present only when this context represents one group of a GROUP-type stage. */
  group?: ResolverStandingsGroup;
  /** Teams sorted by table position; index 0 = position 1. */
  standingTeams: readonly ResolverTeam[];
  templateSubtypes?: readonly string[];
}

/**
 * The per-championship statistics `championshipStatsByPlayer` returns for one
 * player. Every field is `Int!` in the schema, so none are optional here — a
 * placeholder for a player with 0 goals must render "0", never be dropped.
 */
export interface ResolverPlayerStats {
  games: number;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
}

/**
 * One ranked row of a player-statistics render. `team`, `positionLabel` and
 * `player.number` come from the player's accepted championship application and
 * are absent together when they have none — the stats are still real, so the row
 * keeps its rank and only the team/number/amplua placeholders drop (the same
 * "data availability, not an authoring mistake" rule as GROUP_NAME).
 */
export interface ResolverRankedPlayer {
  player: ResolverPlayer;
  team?: ResolverTeam;
  /** Amplua, already mapped to its display label — see domain/player-stats. */
  positionLabel?: string;
  stats: ResolverPlayerStats;
}

/**
 * A championship's players ranked by one statistic — used by the
 * player-statistics template, which has no match and no standings table. A
 * placeholder's `playerNumber` addresses a rank slot in `rankedPlayers` (1 =
 * top), mirroring how `teamNumber` addresses `standingTeams` and `matchNumber`
 * addresses `matches`.
 *
 * `teamNumber` is unused in this mode: team name/short name/logo resolve
 * relative to the player occupying the slot.
 */
export interface PlayerStatsResolverContext {
  mode: 'player_stats';
  tournament: ResolverTournament;
  championship: ResolverChampionship;
  /** Sorted by the active ranking and already sliced to top-N; index 0 = rank 1. */
  rankedPlayers: readonly ResolverRankedPlayer[];
  templateSubtypes?: readonly string[];
}

export type ResolverContext = MatchResolverContext | StandingsResolverContext | PlayerStatsResolverContext;

/** Every render mode this resolver can address. */
export type ResolverMode = ResolverContext['mode'];

export type ResolvedValue =
  | { type: 'text'; value: string }
  | {
      type: 'image';
      url: string;
      /**
       * Tried once when `url` fails to load. Whether a key has a stand-in image
       * is a per-key product rule, so it is decided here rather than in the
       * render step, which only knows to retry with this URL before dropping the
       * placeholder.
       */
      fallbackUrl?: string;
    };

export function resolvePlaceholder(data: PlaceholderData, ctx: ResolverContext): ResolvedValue | null {
  if (!isKnownPlaceholderKey(data.placeholderKey)) return null;
  const key = data.placeholderKey as PlaceholderKey;

  switch (ctx.mode) {
    case 'standings':
      return resolveStandingsPlaceholder(key, data, ctx);
    case 'player_stats':
      return resolvePlayerStatsPlaceholder(key, data, ctx);
    case 'match':
      return resolveMatchPlaceholder(key, data, ctx);
    default:
      return assertNeverMode(ctx);
  }
}

/**
 * A new `ResolverContext` variant is a compile error here rather than a silent
 * fall-through to whichever branch happened to be the `else`. At runtime an
 * unreachable mode drops the placeholder, matching every other "cannot resolve"
 * path — a headless render must not throw over one bad object.
 */
function assertNeverMode(ctx: never): null {
  console.warn('[resolver] unhandled ResolverContext mode', ctx);
  return null;
}

function resolveMatchPlaceholder(key: PlaceholderKey, data: PlaceholderData, ctx: MatchResolverContext): ResolvedValue | null {
  const slot = pickMatchSlot(data.matchNumber, ctx);
  if (slot === null) return null;
  if (ctx.goal) {
    const value = resolveGoalPlaceholder(key, data, ctx.goal, slot);
    if (value !== undefined) return value;
  }
  const teamSlot = pickTeam(data.teamNumber, slot);

  switch (key) {
    case PlaceholderKey.TEAM_NAME:
      return teamSlot ? text(teamSlot.name) : null;
    case PlaceholderKey.TEAM_SHORT_NAME:
      return teamSlot ? text(teamSlot.shortName) : null;
    case PlaceholderKey.TEAM_LOGO:
      return teamSlot?.logoUrl ? image(teamSlot.logoUrl) : null;
    case PlaceholderKey.SCORE: {
      const template = (data.kind === 'placeholder-text' && data.scoreFormat) || DEFAULT_SCORE_FORMAT;
      const home = String(slot.match.homeScore ?? 0);
      const away = String(slot.match.awayScore ?? 0);
      return text(template.replace(/t1/g, home).replace(/t2/g, away));
    }
    case PlaceholderKey.SCORE_TEAM: {
      if (!data.teamNumber) return null;
      const score = data.teamNumber === 1 ? slot.match.homeScore : slot.match.awayScore;
      return text(String(score ?? 0));
    }
    case PlaceholderKey.CHAMPIONSHIP_NAME:
      return text(ctx.championship.name);
    case PlaceholderKey.TOURNAMENT_NAME:
      return text(ctx.tournament.name);
    case PlaceholderKey.TOURNAMENT_LOGO:
      return ctx.tournament.logoUrl ? image(ctx.tournament.logoUrl) : null;
    case PlaceholderKey.MATCH_DATE: {
      if (!slot.match.date) return null;
      const customFormat = data.kind === 'placeholder-text' ? data.dateFormat?.trim() : undefined;
      return text(safeFormatDate(slot.match.date, customFormat || DEFAULT_DATE_FORMAT));
    }
    case PlaceholderKey.MATCH_TIME:
      return slot.match.date ? text(format(new Date(slot.match.date), 'HH:mm', { locale: ru })) : null;
    case PlaceholderKey.MATCH_VENUE:
      return slot.match.venue ? text(slot.match.venue) : null;
    case PlaceholderKey.ROUND_NAME:
      return slot.match.roundName ? text(slot.match.roundName) : null;
    case PlaceholderKey.PLAYER_NAME: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, slot);
      return player ? text(playerName(player, data)) : null;
    }
    case PlaceholderKey.PLAYER_NUMBER: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, slot);
      return player && typeof player.number === 'number' ? text(String(player.number)) : null;
    }
    case PlaceholderKey.PLAYER_PHOTO: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, slot);
      return player?.photoUrl ? image(player.photoUrl) : null;
    }
    default:
      // Standings-only keys (position, W/D/L, goals, points, …) never resolve
      // in match mode — TOURNAMENT_TABLE is the only type that allows them,
      // and it always renders through the standings branch above.
      return null;
  }
}

/**
 * The keys a video goal plate reads from its goal; `undefined` — the key is not about the goal (championship, round,
 * score), resolve it as on any match.
 */
function resolveGoalPlaceholder(
  key: PlaceholderKey,
  data: PlaceholderData,
  goal: ResolverGoalMoment,
  slot: ResolverMatchSlot,
): ResolvedValue | null | undefined {
  switch (key) {
    case PlaceholderKey.TEAM_NAME:
      return text(goal.team.name);
    case PlaceholderKey.TEAM_SHORT_NAME:
      return text(goal.team.shortName);
    case PlaceholderKey.TEAM_LOGO:
      return goal.team.logoUrl ? image(goal.team.logoUrl) : null;
    case PlaceholderKey.PLAYER_NAME:
      return goal.scorer ? text(playerName(goal.scorer, data)) : null;
    case PlaceholderKey.PLAYER_NUMBER:
      return typeof goal.scorer?.number === 'number' ? text(String(goal.scorer.number)) : null;
    case PlaceholderKey.PLAYER_PHOTO:
      return goal.scorer?.photoUrl ? image(goal.scorer.photoUrl) : null;
    case PlaceholderKey.GOAL_MINUTE:
      return text(goal.minute);
    case PlaceholderKey.SCORE_TEAM: {
      const score = goal.team.id === slot.homeTeam.id ? slot.match.homeScore : slot.match.awayScore;
      return text(String(score ?? 0));
    }
    default:
      return undefined;
  }
}

// Resolves a TOURNAMENT_TABLE placeholder against a match-less standings
// table. `teamNumber` addresses a row/position in `standingTeams` (1 = top
// of the table) rather than home/away — mirrors how `matchNumber` addresses
// `ctx.matches` for multi-match templates.
function resolveStandingsPlaceholder(key: PlaceholderKey, data: PlaceholderData, ctx: StandingsResolverContext): ResolvedValue | null {
  const teamRow = data.teamNumber && data.teamNumber >= 1 ? ctx.standingTeams[data.teamNumber - 1] : undefined;

  switch (key) {
    case PlaceholderKey.TEAM_NAME:
      return teamRow ? text(teamRow.name) : null;
    case PlaceholderKey.TEAM_SHORT_NAME:
      return teamRow ? text(teamRow.shortName) : null;
    case PlaceholderKey.TEAM_LOGO:
      return teamRow?.logoUrl ? image(teamRow.logoUrl) : null;
    case PlaceholderKey.CHAMPIONSHIP_NAME:
      return text(ctx.championship.name);
    case PlaceholderKey.TOURNAMENT_NAME:
      return text(ctx.tournament.name);
    case PlaceholderKey.TOURNAMENT_LOGO:
      return ctx.tournament.logoUrl ? image(ctx.tournament.logoUrl) : null;
    case PlaceholderKey.GROUP_NAME:
      // `ctx.group` is set only for a context built from a GROUP-type stage's
      // team groups. Every other stage type
      // renders one flat table with no group, so this resolves to null and
      // the render step drops the placeholder — the same template can
      // be rendered against both kinds of stage and simply shows nothing here
      // when there is no group to name.
      return ctx.group ? text(ctx.group.name) : null;
    case PlaceholderKey.TOURNAMENT_POSITION:
      return typeof teamRow?.standing?.position === 'number' ? text(String(teamRow.standing.position)) : null;
    case PlaceholderKey.GAMES_PLAYED:
      return typeof teamRow?.standing?.gamesPlayed === 'number' ? text(String(teamRow.standing.gamesPlayed)) : null;
    case PlaceholderKey.GAMES_WON:
      return typeof teamRow?.standing?.gamesWon === 'number' ? text(String(teamRow.standing.gamesWon)) : null;
    case PlaceholderKey.GAMES_LOST:
      return typeof teamRow?.standing?.gamesLost === 'number' ? text(String(teamRow.standing.gamesLost)) : null;
    case PlaceholderKey.GAMES_DRAWN:
      return typeof teamRow?.standing?.gamesDrawn === 'number' ? text(String(teamRow.standing.gamesDrawn)) : null;
    case PlaceholderKey.GOALS_SCORED:
      return typeof teamRow?.standing?.goalsScored === 'number' ? text(String(teamRow.standing.goalsScored)) : null;
    case PlaceholderKey.GOALS_CONCEDED:
      return typeof teamRow?.standing?.goalsConceded === 'number' ? text(String(teamRow.standing.goalsConceded)) : null;
    case PlaceholderKey.GOAL_DIFFERENCE: {
      const s = teamRow?.standing;
      if (typeof s?.goalsScored !== 'number' || typeof s?.goalsConceded !== 'number') return null;
      return text(String(s.goalsScored - s.goalsConceded));
    }
    case PlaceholderKey.GAMES_DIFFERENCE: {
      const s = teamRow?.standing;
      if (typeof s?.gamesWon !== 'number' || typeof s?.gamesLost !== 'number') return null;
      return text(String(s.gamesWon - s.gamesLost));
    }
    case PlaceholderKey.POINTS:
      return typeof teamRow?.standing?.points === 'number' ? text(String(teamRow.standing.points)) : null;
    default:
      // Match-only keys (SCORE, MATCH_DATE, PLAYER_*, …) never resolve
      // against a standings table.
      return null;
  }
}

/**
 * Resolves a player-statistics placeholder against a championship-wide ranking.
 * `playerNumber` addresses a rank slot (1 = top) rather than a roster index —
 * out of range returns null, so a slot-5 placeholder on a top-3 render doesn't
 * fall back to rank 1. `teamNumber` is never read: the team is whichever team
 * the ranked player plays for.
 */
function resolvePlayerStatsPlaceholder(
  key: PlaceholderKey,
  data: PlaceholderData,
  ctx: PlayerStatsResolverContext,
): ResolvedValue | null {
  const row = data.playerNumber && data.playerNumber >= 1 ? ctx.rankedPlayers[data.playerNumber - 1] : undefined;

  switch (key) {
    case PlaceholderKey.TEAM_NAME:
      return row?.team ? text(row.team.name) : null;
    case PlaceholderKey.TEAM_SHORT_NAME:
      return row?.team ? text(row.team.shortName) : null;
    case PlaceholderKey.TEAM_LOGO:
      return row?.team?.logoUrl ? image(row.team.logoUrl) : null;
    case PlaceholderKey.CHAMPIONSHIP_NAME:
      return text(ctx.championship.name);
    case PlaceholderKey.TOURNAMENT_NAME:
      return text(ctx.tournament.name);
    case PlaceholderKey.TOURNAMENT_LOGO:
      return ctx.tournament.logoUrl ? image(ctx.tournament.logoUrl) : null;
    case PlaceholderKey.PLAYER_NAME:
      return row ? text(playerName(row.player, data)) : null;
    case PlaceholderKey.PLAYER_NUMBER:
      return typeof row?.player.number === 'number' ? text(String(row.player.number)) : null;
    case PlaceholderKey.PLAYER_PHOTO: {
      // The player's team logo stands in for a missing photo — in a leaderboard
      // every rank slot is authored identically, so one player without a photo
      // would otherwise punch a hole in an otherwise full row. It covers both
      // failure shapes: no photo URL at all (resolved to the logo outright) and
      // a photo URL that fails to load (the logo rides along as `fallbackUrl`,
      // since only the substitution step learns the load failed).
      if (!row) return null;
      const teamLogoUrl = row.team?.logoUrl;
      if (!row.player.photoUrl) return teamLogoUrl ? image(teamLogoUrl) : null;
      return image(row.player.photoUrl, teamLogoUrl);
    }
    case PlaceholderKey.PLAYER_POSITION:
      return row?.positionLabel ? text(row.positionLabel) : null;
    // Stats are non-optional numbers, so `row` is the only thing to check — a
    // player with 0 goals renders "0" rather than being dropped.
    case PlaceholderKey.PLAYER_GAMES_PLAYED:
      return row ? text(String(row.stats.games)) : null;
    case PlaceholderKey.PLAYER_GOALS:
      return row ? text(String(row.stats.goals)) : null;
    case PlaceholderKey.PLAYER_ASSISTS:
      return row ? text(String(row.stats.assists)) : null;
    case PlaceholderKey.PLAYER_GOALS_ASSISTS:
      return row ? text(String(row.stats.goals + row.stats.assists)) : null;
    case PlaceholderKey.PLAYER_YELLOW_CARDS:
      return row ? text(String(row.stats.yellowCards)) : null;
    case PlaceholderKey.PLAYER_RED_CARDS:
      return row ? text(String(row.stats.redCards)) : null;
    default:
      // Match-only (SCORE, MATCH_DATE, …) and standings-only (POINTS,
      // TOURNAMENT_POSITION, GROUP_NAME, …) keys never resolve here.
      return null;
  }
}

// Returns the per-match slot to resolve against. When `matchNumber` is set and
// `ctx.matches` is non-empty, picks `matches[matchNumber-1]` — returns `null`
// (drop the placeholder) when the index is out of range, so an editor that
// asks for match #4 of a 3-match render doesn't silently fall through to
// match #1. When `matchNumber` is unset or `ctx.matches` is empty, falls
// back to the top-level single-match fields to preserve SCHEDULE/RESULTS.
function pickMatchSlot(matchNumber: number | undefined, ctx: MatchResolverContext): ResolverMatchSlot | null {
  if (matchNumber && matchNumber >= 1) {
    const slot = ctx.matches?.[matchNumber - 1];
    return slot ?? null;
  }
  return {
    match: ctx.match,
    homeTeam: ctx.homeTeam,
    awayTeam: ctx.awayTeam,
    homeRoster: ctx.homeRoster,
    awayRoster: ctx.awayRoster,
  };
}

function pickTeam(teamNumber: number | undefined, slot: ResolverMatchSlot): ResolverTeam | undefined {
  if (teamNumber === 1) return slot.homeTeam;
  if (teamNumber === 2) return slot.awayTeam;
  return undefined;
}

function pickPlayer(
  teamNumber: number | undefined,
  playerNumber: number | undefined,
  slot: ResolverMatchSlot,
): ResolverPlayer | undefined {
  if (!playerNumber || playerNumber < 1) return undefined;
  const roster = teamNumber === 1 ? slot.homeRoster : teamNumber === 2 ? slot.awayRoster : undefined;
  return roster?.[playerNumber - 1];
}

/**
 * Applies the placeholder's `nameFormat` to a player. Falls back to the
 * pre-assembled `name` both when the source has no parts and when the chosen
 * preset yields nothing (e.g. "только фамилия" for a player recorded with a
 * given name only) — an author picking a format should never be able to blank
 * out a name that the data can otherwise show.
 */
function playerName(player: ResolverPlayer, data: PlaceholderData): string {
  if (!player.nameParts) return player.name;
  const nameFormat = data.kind === 'placeholder-text' ? data.nameFormat : undefined;
  return formatPlayerNameParts(player.nameParts, nameFormat) || player.name;
}

function text(value: string): ResolvedValue {
  return { type: 'text', value };
}

// `fallbackUrl` is omitted rather than set to `undefined` so a value with no
// stand-in stays deep-equal to a plain `{ type: 'image', url }`.
function image(url: string, fallbackUrl?: string): ResolvedValue {
  return fallbackUrl ? { type: 'image', url, fallbackUrl } : { type: 'image', url };
}

export const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';
export const DEFAULT_SCORE_FORMAT = 't1:t2';

function safeFormatDate(iso: string, pattern: string): string {
  try {
    return format(new Date(iso), pattern, { locale: ru });
  } catch {
    return format(new Date(iso), DEFAULT_DATE_FORMAT, { locale: ru });
  }
}
